import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Bold, Italic } from '@ckeditor/ckeditor5-basic-styles';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { Font } from '@ckeditor/ckeditor5-font';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
import ImagePlugin from '@ckeditor/ckeditor5-image/src/image';
import ImageUploadPlugin from '@ckeditor/ckeditor5-image/src/imageupload';
import { Link } from '@ckeditor/ckeditor5-link';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import type { EditorConfig } from '@ckeditor/ckeditor5-core/src/editor/editorconfig';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCaption from '@ckeditor/ckeditor5-table/src/tablecaption.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize.js';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import { SimpleUploadAdapter } from '@ckeditor/ckeditor5-upload';

type ExtendedEditorConfig = EditorConfig & {
	table?: {
		contentToolbar?: string[];
	};
	simpleUpload?: {
		uploadUrl?: string;
		withCredentials?: boolean;
		headers?: Record<string, string>;
	};
	mediaEmbed?: {
		previewsInData?: boolean;
	};
	link?: {
		defaultProtocol?: string;
		addTargetToExternalLinks?: boolean;
		decorators?: Record<
			string,
			{
				mode: 'automatic' | 'manual';
				callback?: (url?: string | null) => boolean;
				attributes?: Record<string, string>;
			}
		>;
	};
};

export const editorConfig: ExtendedEditorConfig = {
	plugins: [
		Alignment,
		Essentials,
		Bold,
		Font,
		Italic,
		Link,
		MediaEmbed,
		Paragraph,
		SimpleUploadAdapter,
		ImageUploadPlugin,
		ImagePlugin,
		FontColor,
		Underline,
		Strikethrough,
		Table,
		TableCaption,
		TableCellProperties,
		TableColumnResize,
		TableProperties,
		TableToolbar,
	],
	placeholder: '내용을 입력해 주세요.',
	toolbar: {
		items: [
			'undo',
			'redo',
			'bold',
			'italic',
			'underline',
			'strikeThrough',
			'fontSize',
			'fontColor',
			'alignment',
			'insertTable',
			'imageUpload',
			'mediaEmbed',
			'link',
		],
	},
	link: {
		defaultProtocol: 'https://',
		addTargetToExternalLinks: true,
		decorators: {
			openInNewTab: {
				mode: 'automatic',
				callback: (url?: string | null) => !!url && /^(https?:)?\/\//.test(url),
				attributes: {
					target: '_blank',
					rel: 'noopener noreferrer',
				},
			},
		},
	},
	mediaEmbed: {
		previewsInData: true,
	},

	simpleUpload: {
		// The URL that the images are uploaded to.
		uploadUrl: `${import.meta.env.VITE_IMAGE_BASE_URL}`,

		// Enable the XMLHttpRequest.withCredentials property.
		withCredentials: false,

		// Headers sent along with the XMLHttpRequest to the upload server.
		headers: {
			'X-CSRF-TOKEN': 'CSRF-Token',
			Authorization: 'Bearer <JSON Web Token>',
		},
	},

	table: {
		contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableCellProperties', 'tableProperties'],
	},
};
